/**
 * A game's public rules page, for the rules panel: its status (published, the
 * link, the scheduled start and description, the /game posts), and the admin's
 * actions on it. Addressed by the game's database id; every call is answered
 * on its ack.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSocketContext } from '@swtorbit/shared-react-hooks';
import { SocketEvent, type Game, type RulesStatus } from '@swtorbit/shared-socket-events';

const TIMEOUT_MS = 30_000;

export interface GameRulesActions {
  status: RulesStatus | null;
  loading: boolean;
  /** The last action's error, until the next one. */
  error: string | null;
  refresh: () => void;
  publish: () => Promise<boolean>;
  unpublish: () => Promise<boolean>;
  /** Save the scheduled start (epoch ms, or null) and description (blank clears it). */
  updateInfo: (scheduledStartAtUtcMs: number | null, description: string | null) => Promise<boolean>;
}

export function useGameRules(game: Pick<Game, 'dbId' | 'publicRules'> | null): GameRulesActions {
  const { socket } = useSocketContext();
  const [status, setStatus] = useState<RulesStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gameId = game?.dbId ?? null;
  const gameIdRef = useRef(gameId);
  gameIdRef.current = gameId;

  const statusCall = useCallback(
    async (event: SocketEvent, payload: object = {}): Promise<boolean> => {
      const forGame = gameIdRef.current;
      if (!socket || forGame === null) return false;
      setLoading(true);
      setError(null);
      const response = await new Promise<RulesStatus>((resolve) => {
        socket.timeout(TIMEOUT_MS).emit(event, { gameId: forGame, ...payload }, (err: Error | null, answer?: RulesStatus) => {
          resolve(err || !answer ? ({ success: false, error: 'The server did not answer' } as RulesStatus) : answer);
        });
      });
      if (gameIdRef.current !== forGame) return false;
      setLoading(false);
      if (response.success) setStatus(response);
      else setError(response.error ?? 'Something went wrong');
      return response.success;
    },
    [socket],
  );

  const refresh = useCallback(() => void statusCall(SocketEvent.RULES_ADMIN_GET_STATUS), [statusCall]);

  // Again when the game changes, or its page is published elsewhere (/game in Discord).
  useEffect(() => {
    setStatus(null);
    if (gameId !== null) refresh();
  }, [gameId, game?.publicRules?.publishedAtUtcMs, refresh]);

  return {
    status,
    loading,
    error,
    refresh,
    publish: () => statusCall(SocketEvent.RULES_ADMIN_PUBLISH),
    unpublish: () => statusCall(SocketEvent.RULES_ADMIN_UNPUBLISH),
    updateInfo: (scheduledStartAtUtcMs, description) =>
      statusCall(SocketEvent.RULES_ADMIN_UPDATE_INFO, { scheduledStartAtUtcMs, description }),
  };
}
